import { createFileRoute } from '@tanstack/react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowRight,
  faBolt,
  faBoxesStacked,
  faBoxOpen,
  faBuilding,
  faCheck,
  faChevronDown,
  faClipboardList,
  faHand,
  faLayerGroup,
  faTag,
  faTruckFast,
  faTriangleExclamation,
  faWarehouse,
} from '@fortawesome/free-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'

import { Footer, LogoLockup, WA_NUMBER } from '../components/SiteChrome'
import { BrandEasyOrder } from '../components/BrandEasyOrder'
import { BrandTrustSections, type TrustQuestion } from '../components/BrandTrustSections'
import { GloveBrandAlternatives } from '../components/GloveBrandAlternatives'
import { whatsAppCtaProps } from '../lib/analytics'
import { getStorefrontConfig, getStorefrontProduct, listStorefrontCategories, toExternalImage } from '../lib/dropify'
import type { StorefrontCategory, StorefrontConfig, StorefrontProduct } from '../lib/dropify'
import { INDEXABLE_ROBOTS, absoluteUrl, brandTitle, buildBrandLandingSchema, buildBreadcrumbSchema, buildFaqSchema } from '../lib/seo'
import { getBrandProofImages } from '../lib/testimonials'
import { listDashboardNewsPosts } from '../lib/news'
import type { FaqItem } from '../lib/seo'

const PAGE_PATH = '/sarung-tangan-diamond'
const PAGE_CATEGORY = 'Sarung Tangan Diamond'
const PRODUCT_SLUG = 'sarung-tangan-diamond'
const PRODUCT_IMAGE = toExternalImage(
  'https://s3.us-east-2.amazonaws.com/8grams-vorme/704f03f1-2dce-4e83-827e-349562728803/2026/06/11/c9c7d0901ffa450fb195938189e99096.jpeg',
  { width: 1024, format: 'webp', quality: 78 },
)

const USE_CASES = [
  { icon: faWarehouse, title: 'Gudang', desc: 'Sortir, penyimpanan, dan pemindahan karton atau barang kering dengan risiko ringan.' },
  { icon: faBoxOpen, title: 'Pengemasan', desc: 'Melipat kardus, menyusun kemasan, dan menyiapkan barang untuk pengiriman.' },
  { icon: faBuilding, title: 'Produksi umum', desc: 'Pekerjaan rutin yang membutuhkan lapisan rajut dan grip tambahan pada telapak.' },
  { icon: faHand, title: 'Bongkar muat ringan', desc: 'Membantu cengkeraman saat menangani kemasan atau material kering yang tidak tajam.' },
]

const LIMITATIONS = [
  'Risiko sayatan, tusukan, atau abrasi tinggi yang membutuhkan kode standar terverifikasi.',
  'Bahan kimia, oli, cairan, dan pekerjaan pada permukaan yang sangat basah.',
  'Panas tinggi, pengelasan, dan pekerjaan kelistrikan.',
  'Area dekat poros, gear, atau komponen mesin yang sedang berputar.',
]

const DROPIFY_REASONS = [
  { icon: faBolt, title: 'Foto aktual sebelum PO', desc: 'Minta admin mengirim foto barang, kemasan, dan label agar tim Anda memastikan spesifikasi sebelum menyetujui pesanan.' },
  { icon: faBoxesStacked, title: 'Sample tersedia', desc: 'Sample dihitung harga satuan ditambah ongkir. Gunakan untuk mengecek rajutan, ukuran, bintik PVC, dan kenyamanan sebelum order besar.' },
  { icon: faTag, title: 'Harga per lusin mengikuti volume', desc: 'Harga dihitung berdasarkan jumlah pembelian. Semakin besar kebutuhan, semakin baik penawaran yang dapat disusun.' },
  { icon: faTruckFast, title: 'Kirim ke pabrik, gudang, atau toko safety', desc: 'Pesanan dapat dikirim ke berbagai lokasi di seluruh Indonesia dengan ekspedisi yang disesuaikan.' },
]

const DIAMOND_TRUST_QUESTIONS: TrustQuestion[] = [
  {
    q: 'Apakah Diamond tersedia dalam ukuran selain all size?',
    a: 'Saat ini yang tersedia adalah all size. Jika pekerjaan Anda membutuhkan ukuran spesifik, sampaikan ke admin agar diarahkan ke seri yang sesuai.',
  },
  {
    q: 'Bisa kirim foto kemasan dan label sebelum PO?',
    a: 'Bisa. Minta admin mengirim foto bagian depan, belakang, kemasan, label, warna, dan marking produk sebelum pesanan dikonfirmasi.',
  },
  {
    q: 'Apakah tersedia dokumen standar untuk tender?',
    a: 'Ketersediaan dokumen perlu dicek pada stok yang akan dipesan. Kirim standar wajib dari tim K3 atau dokumen tender. Admin akan mengecek marking dan dokumen produk sebelum Anda mencantumkannya dalam PO.',
  },
  {
    q: 'Bagaimana menguji Diamond sebelum order lusinan?',
    a: 'Pesan beberapa pasang sebagai sample dengan harga satuan plus ongkir. Cek rajutan, bintik PVC, dan kenyamanannya di pekerjaan nyata sebelum ambil jumlah grosir.',
  },
  {
    q: 'Apa ciri sarung tangan Diamond untuk kebutuhan gudang?',
    a: 'Diamond menggunakan rajut abu-abu dengan bintik PVC kuning pada telapak dan jari. Produk ini dapat dipertimbangkan untuk memegang barang kering dalam pekerjaan gudang dan pengemasan dengan risiko ringan.',
  },
]

export const DIAMOND_FAQ: FaqItem[] = [
  {
    q: 'Apa bahan dan bentuk sarung tangan Diamond?',
    a: 'Sarung tangan Diamond menggunakan rajutan abu-abu dengan bintik PVC kuning pada telapak dan jari. Bintik tersebut membantu cengkeraman saat memegang barang kering. Anda dapat meminta foto stok aktual untuk memeriksa warna, rajutan, dan kondisi barang sebelum memesan.',
  },
  {
    q: 'Sarung tangan Diamond cocok untuk pekerjaan apa?',
    a: 'Diamond cocok dipertimbangkan untuk pengemasan, pekerjaan gudang, produksi umum, dan pemindahan barang kering dengan risiko ringan. Jika pekerjaan melibatkan benda tajam, bahan kimia, oli, panas, atau kelistrikan, sampaikan risikonya kepada admin supaya Anda tidak salah memilih sarung tangan.',
  },
  {
    q: 'Berapa minimum order sarung tangan Diamond?',
    a: 'Minimum ordernya mulai 25 lusin atau 300 pasang, dengan isi 12 pasang per lusin. Sebelum membayar, minta admin memastikan jumlah stok yang tersedia dan jadwal barang dapat diproses.',
  },
  {
    q: 'Apakah bisa memesan sample Diamond?',
    a: 'Bisa ditanyakan kepada admin. Sample membantu tim Anda memeriksa rajutan, ukuran, bintik PVC, dan kenyamanan saat memegang benda kerja sebelum membeli dalam jumlah besar.',
  },
  {
    q: 'Apakah Diamond memiliki standar anti-sayat atau tahan bahan kimia?',
    a: 'Dokumen yang tersedia belum mendukung klaim anti-sayat atau tahan bahan kimia untuk sarung tangan Diamond ini. Jika pekerjaan Anda mensyaratkan perlindungan tersebut, kirim standar wajib kepada admin agar diarahkan ke seri yang memiliki dokumen sesuai.',
  },
  {
    q: 'Bagaimana cara meminta harga dan stok?',
    a: 'Kirim jumlah lusin, jumlah pekerja, jenis pekerjaan, kota tujuan, dan tanggal barang dibutuhkan melalui WhatsApp. Admin akan mengirim foto barang aktual, mengecek stok, lalu memberikan harga serta pilihan pengiriman.',
  },
]

export const Route = createFileRoute('/sarung-tangan-diamond')({
  loader: async () => {
    const [config, categories, product, proofImages, newsPosts] = await Promise.all([
      getStorefrontConfig().catch(() => null),
      listStorefrontCategories().catch(() => [] as StorefrontCategory[]),
      getStorefrontProduct(PRODUCT_SLUG).catch(() => null),
      getBrandProofImages(),
      listDashboardNewsPosts().catch(() => []),
    ])
    return { config, categories, product, proofImages, newsPosts }
  },
  head: () => {
    const title = brandTitle('Grosir Sarung Tangan Diamond')
    const description =
      'Grosir sarung tangan Diamond rajut abu-abu bintik PVC kuning untuk gudang & packing. MOQ 25 lusin, sample, kirim se-Indonesia. Cek stok via WhatsApp.'
    return {
      meta: [
        { title },
        { name: 'description', content: description },
        { name: 'robots', content: INDEXABLE_ROBOTS },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:url', content: absoluteUrl(PAGE_PATH) },
        { property: 'og:locale', content: 'id_ID' },
        { property: 'og:image', content: PRODUCT_IMAGE },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: PRODUCT_IMAGE },
      ],
      links: [{ rel: 'canonical', href: absoluteUrl(PAGE_PATH) }],
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildBrandLandingSchema({ brandName: 'Diamond', pageName: title, description, path: PAGE_PATH })),
        },
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildBreadcrumbSchema([
            { name: 'Beranda', path: '/' },
            { name: 'Merek', path: '/merek' },
            { name: 'Sarung Tangan Diamond', path: PAGE_PATH },
          ])),
        },
        { type: 'application/ld+json', children: JSON.stringify(buildFaqSchema([...DIAMOND_FAQ, ...DIAMOND_TRUST_QUESTIONS])) },
      ],
    }
  },
  component: DiamondRoute,
})

function DiamondRoute() {
  const { config, categories, product, proofImages, newsPosts } = Route.useLoaderData()
  return <DiamondLandingPage config={config} categories={categories} product={product} proofImages={proofImages} newsPosts={newsPosts} />
}

function waLink(message: string) {
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`
}

function WhatsAppButton({ message, label, placement, className }: { message: string; label: string; placement: string; className: string }) {
  return (
    <a
      href={waLink(message)}
      target="_blank"
      rel="noopener noreferrer"
      {...whatsAppCtaProps('landing_page', { cta_placement: placement, lp_category: PAGE_CATEGORY })}
      className={className}
    >
      <FontAwesomeIcon icon={faWhatsapp} className="h-5 w-5 flex-shrink-0" />
      {label}
    </a>
  )
}

function SectionLabel({ children }: { children: string }) {
  return <p className="text-xs font-black uppercase tracking-[0.22em] text-yellow-700">{children}</p>
}

export function DiamondLandingPage({ config = null, categories = [], product: _product = null, proofImages = [], newsPosts = [] }: { config?: StorefrontConfig | null; categories?: StorefrontCategory[]; product?: StorefrontProduct | null; proofImages?: string[]; newsPosts?: import('../lib/dropify').StorefrontBlogPost[] }) {
  const heroMessage =
    'Halo Dropify, saya mau cek sarung tangan Diamond untuk ___. Kebutuhan: ___ lusin, kirim ke ___. Bisa cek stok dan harga grosirnya?'

  return (
    <div className="min-h-screen bg-[#f7f7f4] text-slate-950">
      <div className="bg-yellow-300 px-4 py-2 text-center text-xs font-black text-[#24231f] sm:text-sm">MOQ mulai 25 lusin · 1 lusin isi 12 pasang · Sample tersedia</div>
      <header className="border-b border-white/10 bg-[#24231f] text-white">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4">
          <a href="/" aria-label="Kembali ke Dropify"><LogoLockup size="sm" invert /></a>
          <WhatsAppButton message={heroMessage} label="Periksa stok Diamond" placement="header" className="inline-flex items-center gap-2 rounded-full border border-yellow-300/60 px-4 py-2.5 text-sm font-bold text-yellow-200 transition-colors hover:bg-yellow-300 hover:text-[#24231f]" />
        </div>
      </header>

      <main>
        <section className="relative overflow-hidden bg-[#24231f] text-white">
          <div aria-hidden className="absolute -right-32 -top-32 h-[34rem] w-[34rem] rotate-45 rounded-[5rem] border-[90px] border-yellow-300/5" />
          <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 py-14 md:py-20 lg:grid-cols-[0.95fr_1.05fr]">
            <div>
              <p className="inline-flex rounded-full bg-yellow-300 px-4 py-2 text-xs font-black uppercase tracking-wider text-[#24231f]">Rajut abu · bintik PVC kuning</p>
              <h1 className="mt-6 text-4xl font-black leading-[1.04] tracking-tight sm:text-5xl lg:text-6xl">Sarung tangan Diamond untuk <span className="text-yellow-300">pengemasan dan kerja gudang.</span></h1>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 md:text-lg">Rajut abu-abu dengan bintik PVC kuning untuk membantu cengkeraman saat menangani karton, kemasan, dan barang kering. Minimum order mulai 25 lusin.</p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <WhatsAppButton message={heroMessage} label="Cek stok & harga Diamond" placement="hero_primary" className="inline-flex items-center justify-center gap-2.5 rounded-xl bg-green-700 px-6 py-4 text-base font-black text-white shadow-lg shadow-black/20 transition-colors hover:bg-green-800" />
                <a href="#produk-diamond" className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 px-6 py-4 text-sm font-black transition-colors hover:bg-white/10">Lihat produk Diamond <FontAwesomeIcon icon={faArrowRight} className="h-4 w-4" /></a>
              </div>
            </div>
            <div data-hero-product-visual className="mx-auto w-full max-w-xl overflow-hidden rounded-[2rem] border-4 border-white bg-white shadow-2xl">
              <img src={PRODUCT_IMAGE} alt="Sarung tangan Diamond rajut abu-abu bintik PVC kuning" width="1024" height="768" fetchPriority="high" decoding="async" className="aspect-[4/3] w-full object-contain" />
            </div>
          </div>
        </section>

        <section className="border-b border-slate-200 bg-white py-16 md:py-20">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 lg:grid-cols-[0.78fr_1.22fr] lg:gap-16">
            <div><SectionLabel>Kenali produknya</SectionLabel><h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Apa itu sarung tangan Diamond?</h2></div>
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="rounded-3xl bg-[#f7f7f4] p-6"><FontAwesomeIcon icon={faLayerGroup} className="h-6 w-6 text-yellow-700" /><h3 className="mt-5 text-xl font-black">Bahan dasar rajut</h3><p className="mt-3 text-sm leading-relaxed text-slate-600">Rajutan abu menutup tangan saat pekerja memegang kemasan, karton, dan material kering dengan risiko ringan.</p></div>
              <div className="rounded-3xl bg-[#24231f] p-6 text-white"><FontAwesomeIcon icon={faHand} className="h-6 w-6 text-yellow-300" /><h3 className="mt-5 text-xl font-black">Bintik PVC pada telapak</h3><p className="mt-3 text-sm leading-relaxed text-slate-300">Bintik kuning pada telapak dan jari membantu menambah cengkeraman dibanding rajutan polos.</p></div>
              <p className="sm:col-span-2 text-base leading-relaxed text-slate-600">Diamond digunakan untuk pekerjaan umum dengan risiko ringan. Untuk pekerjaan yang mensyaratkan standar keselamatan tertentu, sampaikan standar tersebut kepada admin sebelum memesan.</p>
            </div>
          </div>
        </section>

        <BrandEasyOrder newsPosts={newsPosts} />

        <section id="produk-diamond" className="scroll-mt-6 bg-[#f7f7f4] py-16 md:py-20">
          <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 lg:grid-cols-2">
            <a href={`/produk/${PRODUCT_SLUG}`} aria-label="Lihat detail Sarung Tangan Diamond" className="group overflow-hidden rounded-[2rem] border border-slate-200 bg-white"><img src={PRODUCT_IMAGE} alt="Sarung tangan Diamond" width="900" height="675" loading="lazy" decoding="async" className="aspect-[4/3] w-full object-contain transition-transform duration-500 group-hover:scale-105" /></a>
            <div>
              <SectionLabel>Produk yang kami jual</SectionLabel>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl"><a href={`/produk/${PRODUCT_SLUG}`} className="hover:text-yellow-700">Sarung Tangan Diamond</a></h2>
              <p className="mt-4 text-base leading-relaxed text-slate-600">Sarung tangan Diamond memakai rajutan abu-abu dengan bintik PVC kuning pada telapak dan jari untuk membantu cengkeraman saat menangani karton, kemasan, dan barang kering.</p>
              <ul className="mt-6 space-y-3 text-sm leading-relaxed text-slate-600">
                {['Dijual dalam jumlah grosir mulai 25 lusin.', 'Satu lusin berisi 12 pasang.', 'Foto barang aktual dapat diminta melalui admin.', 'Ketersediaan sample dapat ditanyakan sebelum pembelian besar.'].map((item) => <li key={item} className="flex gap-3"><FontAwesomeIcon icon={faCheck} className="mt-1 h-4 w-4 flex-shrink-0 text-yellow-700" />{item}</li>)}
              </ul>
              <WhatsAppButton message={heroMessage} label="Tanya harga & stok" placement="product" className="mt-7 inline-flex items-center justify-center gap-2 rounded-xl bg-green-700 px-6 py-4 text-base font-black text-white transition-colors hover:bg-green-800" />
            </div>
          </div>
        </section>

        <section className="border-y border-slate-200 bg-white py-16 md:py-20">
          <div className="mx-auto grid max-w-7xl gap-8 px-4 lg:grid-cols-[1.12fr_0.88fr]">
            <div className="rounded-[2rem] border border-slate-200 p-6 md:p-8">
              <SectionLabel>Cocok digunakan untuk</SectionLabel><h2 className="mt-3 text-3xl font-black tracking-tight">Kerja kering dengan risiko ringan</h2>
              <div className="mt-7 grid gap-4 sm:grid-cols-2">{USE_CASES.map((item) => <article key={item.title} className="rounded-2xl bg-[#f7f7f4] p-5"><FontAwesomeIcon icon={item.icon} className="h-5 w-5 text-yellow-700" /><h3 className="mt-4 font-black">{item.title}</h3><p className="mt-2 text-sm leading-relaxed text-slate-600">{item.desc}</p></article>)}</div>
            </div>
            <aside className="rounded-[2rem] border border-amber-200 bg-amber-50 p-6 md:p-8">
              <p className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em] text-amber-700"><FontAwesomeIcon icon={faTriangleExclamation} className="h-4 w-4" />Batas pemakaian</p><h2 className="mt-3 text-3xl font-black tracking-tight text-amber-950">Gunakan seri dengan spesifikasi khusus</h2>
              <div className="mt-7 space-y-3">{LIMITATIONS.map((item) => <div key={item} className="flex gap-3 rounded-2xl border border-amber-200 bg-white/80 p-4 text-sm leading-relaxed text-amber-950"><FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />{item}</div>)}</div>
            </aside>
          </div>
        </section>

        <section className="bg-white py-16 md:py-20">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
            <div><SectionLabel>Pemesanan grosir</SectionLabel><h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Satu admin menangani pengecekan stok, penawaran harga, dan pengiriman</h2><p className="mt-4 text-base leading-relaxed text-slate-600">Kirim jumlah lusin, jenis pekerjaan, dan kota tujuan. Admin akan mengecek ketersediaan barang dan menyiapkan rincian pesanannya.</p><WhatsAppButton message={heroMessage} label="Chat admin Diamond" placement="why_dropify" className="mt-6 inline-flex items-center gap-2 rounded-xl bg-green-700 px-5 py-3.5 text-sm font-black text-white transition-colors hover:bg-green-800" /></div>
            <div className="grid gap-4 sm:grid-cols-2">{DROPIFY_REASONS.map((item) => <article key={item.title} className="rounded-2xl border border-slate-200 bg-[#f7f7f4] p-5"><div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#24231f] text-yellow-300"><FontAwesomeIcon icon={item.icon} className="h-5 w-5" /></div><h3 className="mt-4 text-lg font-black tracking-tight">{item.title}</h3><p className="mt-2 text-sm leading-relaxed text-slate-600">{item.desc}</p></article>)}</div>
          </div>
        </section>

        <section className="border-y border-slate-200 bg-[#f7f7f4] py-16 md:py-20">
          <div className="mx-auto max-w-4xl px-4"><div className="text-center"><SectionLabel>FAQ Diamond</SectionLabel><h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Pertanyaan sebelum memesan sarung tangan Diamond</h2></div><div className="mt-9 divide-y divide-slate-200 border-y border-slate-200">{DIAMOND_FAQ.map((item) => <details key={item.q} className="group py-5"><summary className="flex cursor-pointer list-none items-center justify-between gap-5 text-left font-black">{item.q}<FontAwesomeIcon icon={faChevronDown} className="h-4 w-4 flex-shrink-0 text-yellow-700 transition-transform group-open:rotate-180" /></summary><p className="mt-3 max-w-3xl pr-8 text-sm leading-relaxed text-slate-600 md:text-base">{item.a}</p></details>)}</div></div>
        </section>

        <section className="bg-yellow-300 py-14 text-[#24231f]"><div className="mx-auto flex max-w-7xl flex-col justify-between gap-7 px-4 lg:flex-row lg:items-center"><div className="max-w-3xl"><p className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em]"><FontAwesomeIcon icon={faClipboardList} className="h-4 w-4" />Pemeriksaan stok dan quotation</p><h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Kirim jumlah lusin, jenis pekerjaan, dan kota tujuan.</h2><p className="mt-3 text-sm leading-relaxed text-[#504b2a] md:text-base">Admin akan memeriksa stok, menyiapkan quotation, dan membandingkan pilihan ekspedisi ke kota tujuan.</p></div><WhatsAppButton message={heroMessage} label="Minta quotation Diamond" placement="final_cta" className="inline-flex flex-shrink-0 items-center justify-center gap-2.5 rounded-xl bg-[#24231f] px-7 py-4 text-base font-black text-white transition-colors hover:bg-slate-800" /></div></section>

        <BrandTrustSections brandName="Diamond" itemName="sarung tangan Diamond" images={proofImages} questions={DIAMOND_TRUST_QUESTIONS} />
        <GloveBrandAlternatives currentPath={PAGE_PATH} />
      </main>
      <Footer config={config} categories={categories} />
    </div>
  )
}
